/**
 * Splits a series into overlaid lines so a chart can draw observed stretches in
 * colour and null (computed/missing) stretches in grey — the "greyed no-data
 * line". A point is observed when its `value` is non-null. All arrays
 * align 1:1 with `points`; each is a separate `<Line connectNulls={false}>` (grey
 * under colour). Pure. See the body for how each null run is bridged.
 *
 * - **`solid`** — observed values (null elsewhere): the coloured line.
 * - **`gap`** — the grey line over every null run (interior → interpolate,
 *   trailing → hold flat, leading → ramp from `baseValue`, the y-axis floor);
 *   null only within observed runs and for an all-null series.
 * - **`gapMarkers`** — interior interpolated points only → hollow ring markers.
 * - **`dots`** — observed points needing a filled dot: interior-gap boundaries and
 *   isolated lone readings. A contiguous daily run gets none (hover dot only).
 */
export interface LinePoint {
  timestamp: number
  value: number | null
}

export interface LineSegments {
  solid: (number | null)[]
  gap: (number | null)[]
  gapMarkers: (number | null)[]
  dots: (number | null)[]
}

export function buildLineSegments(
  points: LinePoint[],
  baseValue = 0,
): LineSegments {
  const n = points.length
  const solid: (number | null)[] = points.map((p) => p.value)
  const gap: (number | null)[] = Array.from({ length: n }, () => null)
  const gapMarkers: (number | null)[] = Array.from({ length: n }, () => null)

  let i = 0
  while (i < n) {
    if (points[i].value != null) {
      i++
      continue
    }

    // Start of a null run [start..end].
    const start = i
    let end = i
    while (end + 1 < n && points[end + 1].value == null) end++

    const leftIdx = start - 1 // last observed before the run, or -1
    const rightIdx = end + 1 // first observed after the run, or n
    const hasLeft = leftIdx >= 0
    const hasRight = rightIdx < n

    const left = hasLeft ? points[leftIdx] : null
    const right = hasRight ? points[rightIdx] : null

    // The run's boundaries are observed points, so their values are non-null;
    // the guards narrow the type without an assertion.
    if (left?.value != null && right?.value != null) {
      // Interior gap: straight bridge from the left observed value to the right.
      const a = left.value
      const b = right.value
      const span = right.timestamp - left.timestamp
      gap[leftIdx] = a
      gap[rightIdx] = b
      for (let k = start; k <= end; k++) {
        const t = points[k].timestamp
        const v = span === 0 ? a : a + ((b - a) * (t - left.timestamp)) / span
        gap[k] = v
        // Interior interpolated point → eligible for a hollow ring marker.
        gapMarkers[k] = v
      }
    } else if (left?.value != null) {
      // Trailing gap: hold the last observed value flat to the chart edge.
      gap[leftIdx] = left.value
      for (let k = start; k <= end; k++) gap[k] = left.value
    } else if (right?.value != null) {
      // Leading gap: ramp from the baseline up to the first observed value, so
      // the line rises out of the chart floor rather than appearing mid-air. The
      // run starts at index 0 (nothing observed precedes it); interpolate on
      // timestamps with the first point pinned at `baseValue`.
      const b = right.value
      const startTs = points[start].timestamp
      const span = right.timestamp - startTs
      gap[rightIdx] = b
      for (let k = start; k <= end; k++) {
        const t = points[k].timestamp
        gap[k] =
          span === 0 ? b : baseValue + ((b - baseValue) * (t - startTs)) / span
      }
    }
    // An all-null series (neither left nor right anchor) leaves `gap` null:
    // there's no observed value to bridge to.

    i = end + 1
  }

  // Filled-dot anchors: an observed point gets a dot when it's isolated (no
  // adjacent observed point, so the solid line can't render it) or it borders an
  // interior gap — `gapMarkers` is non-null only at interior interpolated points,
  // so an observed point next to one is an interior-gap boundary. Points inside a
  // contiguous run, or merely at a leading/trailing boundary, get no dot.
  const dots: (number | null)[] = solid.map((v, k) => {
    if (v == null) return null
    const leftGap = k === 0 || solid[k - 1] == null
    const rightGap = k === n - 1 || solid[k + 1] == null
    if (leftGap && rightGap) return v // isolated lone reading
    if (gapMarkers[k - 1] != null || gapMarkers[k + 1] != null) return v
    return null
  })

  return { solid, gap, gapMarkers, dots }
}
